<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const rawMacros = useMapGetter('macros/getMacros');
const executingMacroId = ref(null);
const scrollContainer = ref(null);
const draggedIndex = ref(null);
const dragOverIndex = ref(null);

const accountId = computed(() => store.getters.getCurrentAccountId);
const storageKey = computed(() => `chatwoot_macro_order_acc_${accountId.value || 'default'}`);

// Natural alphanumeric sort helper (01, 02, 03, A, B, C...)
const naturalSort = (a, b) => {
  const nameA = a?.name || '';
  const nameB = b?.name || '';
  return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
};

// Ordered list of macros ref
const orderedMacros = ref([]);

const initOrderedMacros = () => {
  if (!rawMacros.value || rawMacros.value.length === 0) {
    orderedMacros.value = [];
    return;
  }

  let savedOrder = [];
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (raw) savedOrder = JSON.parse(raw);
  } catch (e) {
    savedOrder = [];
  }

  const all = [...rawMacros.value];

  if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
    const sorted = [];
    // First place macros matching saved order
    savedOrder.forEach(id => {
      const found = all.find(m => m.id === id);
      if (found) sorted.push(found);
    });
    // Then append any new macros that were not in saved order, sorted naturally
    const remaining = all.filter(m => !savedOrder.includes(m.id)).sort(naturalSort);
    orderedMacros.value = [...sorted, ...remaining];
  } else {
    // Default: Sort naturally (01, 02, 03, 04, 05, A, B, C...)
    orderedMacros.value = all.sort(naturalSort);
  }
};

watch(rawMacros, () => {
  initOrderedMacros();
}, { immediate: true, deep: true });

onMounted(() => {
  if (!rawMacros.value || rawMacros.value.length === 0) {
    store.dispatch('macros/get');
  } else {
    initOrderedMacros();
  }
});

const saveCustomOrder = () => {
  try {
    const ids = orderedMacros.value.map(m => m.id);
    localStorage.setItem(storageKey.value, JSON.stringify(ids));
  } catch (e) {
    // ignore
  }
};

// Drag and Drop Handlers
const onDragStart = (event, index) => {
  draggedIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', index);
};

const onDragOver = (event, index) => {
  event.preventDefault();
  dragOverIndex.value = index;
};

const onDrop = (event, targetIndex) => {
  event.preventDefault();
  if (draggedIndex.value === null || draggedIndex.value === targetIndex) {
    draggedIndex.value = null;
    dragOverIndex.value = null;
    return;
  }

  const items = [...orderedMacros.value];
  const [draggedItem] = items.splice(draggedIndex.value, 1);
  items.splice(targetIndex, 0, draggedItem);
  orderedMacros.value = items;

  saveCustomOrder();

  draggedIndex.value = null;
  dragOverIndex.value = null;
};

const onDragEnd = () => {
  draggedIndex.value = null;
  dragOverIndex.value = null;
};

const handleWheel = event => {
  if (!scrollContainer.value) return;
  if (event.deltaY !== 0) {
    event.preventDefault();
    scrollContainer.value.scrollLeft += event.deltaY * 0.9;
  }
};

const executeMacro = async macro => {
  if (executingMacroId.value) return; // Prevent double clicks
  
  try {
    executingMacroId.value = macro.id;
    await store.dispatch('macros/execute', {
      macroId: macro.id,
      conversationIds: [props.conversationId],
    });
    useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
    useAlert(t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
  } catch (error) {
    useAlert(t('MACROS.ERROR'));
  } finally {
    executingMacroId.value = null;
  }
};
</script>

<template>
  <div
    v-if="orderedMacros && orderedMacros.length > 0"
    class="relative flex items-center w-full min-w-0"
  >
    <!-- Container com scroll horizontal e drag & drop -->
    <div
      ref="scrollContainer"
      class="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 w-full pr-3 select-none scroll-smooth"
      @wheel="handleWheel"
    >
      <button
        v-for="(macro, index) in orderedMacros"
        :key="macro.id"
        type="button"
        draggable="true"
        class="macro-pill-btn flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold rounded-full transition-all duration-150 active:scale-95 whitespace-nowrap cursor-grab active:cursor-grabbing select-none"
        :class="{
          'opacity-40 scale-95 border-dashed border-blue-500': draggedIndex === index,
          'ring-2 ring-[#1F93FF] scale-105': dragOverIndex === index && draggedIndex !== index,
        }"
        :title="`${macro.name} (Clique para disparar / Segure para reordenar)`"
        :disabled="executingMacroId === macro.id"
        @dragstart="onDragStart($event, index)"
        @dragover="onDragOver($event, index)"
        @drop="onDrop($event, index)"
        @dragend="onDragEnd"
        @click="executeMacro(macro)"
      >
        <Icon v-if="executingMacroId === macro.id" icon="i-lucide-loader-2" class="size-3.5 animate-spin text-[#1F93FF]" />
        <Icon v-else icon="i-lucide-zap" class="size-3.5 text-[#1F93FF]" />
        <span class="truncate max-w-[220px] leading-tight">{{ macro.name }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.macro-pill-btn {
  background-color: rgba(31, 147, 255, 0.08);
  border: 1px solid rgba(31, 147, 255, 0.35);
  color: #1a7fe6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.macro-pill-btn:hover {
  background-color: rgba(31, 147, 255, 0.16);
  border-color: rgba(31, 147, 255, 0.55);
  color: #0f62c4;
}

:global(.dark) .macro-pill-btn {
  background-color: rgba(31, 147, 255, 0.14);
  border-color: rgba(31, 147, 255, 0.32);
  color: #93c5fd;
}

:global(.dark) .macro-pill-btn:hover {
  background-color: rgba(31, 147, 255, 0.24);
  border-color: rgba(31, 147, 255, 0.55);
  color: #bfdbfe;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
